import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {StorageService} from '../../../services/storage/storage.service';
import {RouteService} from '../../../services/route/route.service';
import {EventSearchFilter, EventService} from '../../../services/event/event.service';
import {UtilService} from '../../../services/util/util.service';
import {AuthService} from '../../../services/auth/auth.service';
import {cloneDeep, find, uniq} from 'lodash-es';
import moment from 'moment';
import {Subscription} from 'rxjs';
import {NavigationEnd, Router} from '@angular/router';
import {CartService} from '../../../services/cart/cart.service';
import {Event, EventStatus, LegalPersonList, VenueGet} from '../../../generated/sales-terminal-client';
import {STATUS_DRAFT} from './event-search.constants';
import {ValidationService} from '../../../services/validation/validation.service';
import {VenueService} from '../../../services/venue/venue.service';
import {ScrollLoad} from '../../../components/scroll-load-component/ScrollLoad';
import {finalize} from 'rxjs/operators';
import {Privileges} from '../../admin/privileges/privileges.constants';
import {SortByOption} from '../../../interfaces/sort-by-option.interface';
import {EventDetailsDialogComponent} from '../../../components/event-details-dialog/event-details-dialog.component';
import {TO_EVENT_ACTION, TO_EVENT_BO_VIEW} from '../sales-terminal.constants';
import {
  EventOwnerWarningDialogComponent
} from '../../../components/event-owner-warning-dialog/event-owner-warning-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {PageableResult} from '../../../components/scroll-load-component/scroll-load.component';
import {LegalPersonService} from '../../../services/legalPerson/legalPerson.service';
import {SelfServiceVenueService} from '../../../services/self-service/venue/self-service-venue.service';
import {CartTypes} from '../../../services/cart/cart-base.service';
import {ViewService} from "../../../services/view/view.service";

@Component({
  selector: 'app-event-search',
  templateUrl: './event-search.component.html',
  styleUrls: ['./event-search.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class EventSearchComponent implements OnInit, OnDestroy, AfterViewInit {
  static STORAGE_SEARCH_RESULTS_VIEW_TYPE = 'searchResults.viewType';
  static STORAGE_SEARCH_RESULTS_PAGE_SIZE = 'searchResults.pageSize';
  static STORAGE_SEARCH_RESULTS_PAGE = 'searchResults.page';
  static STORAGE_SEARCH_RESULTS_SORT_BY = 'searchResults.sortBy';

  @ViewChild('organiserInput') organiserInput: ElementRef<HTMLInputElement>;
  @ViewChild('eventInput') eventInput: ElementRef<HTMLInputElement>;

  public searchTerm: string;
  private _subscriptions: Subscription[] = [];
  private _defaultFilters: EventSearchFilter = {
    dateStart: null,
    dateEnd: null,
    eventId: null,
    organisers: null,
    status: null,
    showId: null,
    eventVenueId: null,
    legalPersonId: null
  };
  public filters: EventSearchFilter = cloneDeep(this._defaultFilters);
  public eventStatuses: EventStatus[] = [];
  public viewType = 'card';
  public results: PageableResult<Event> = {items: [], total: 0};
  public sortByOptions: SortByOption[] = [
    {id: 1, field: 'eventStartUtc', order: 'desc', title: 'general.api_sort_by_newest'},
    {id: 2, field: 'eventStartUtc', order: 'asc', title: 'general.api_sort_by_oldest'}
  ];
  public sortBy: number = this.sortByOptions[1].id;
  public pageSizeOptions: number[] = [10, 20, 30];
  public pageSize: number = this.pageSizeOptions[1];
  public page = 1;
  // "Show loading or show search results"-toggle
  public isLoading = false;
  public isDetailedSearch = true;
  public showMobileFilters: boolean = false;
  public showSelfServiceVenues: boolean = false; // LEGACY

  public venueScrollLoad: ScrollLoad<VenueGet> = this.venueService.getScrollLoad();
  public selfServiceVenueScrollLoad: ScrollLoad<VenueGet> = this.selfServiceVenueService.getScrollLoad();
  public legalPersonScrollLoad: ScrollLoad<LegalPersonList> = this.legalPersonService.getScrollLoad();

  constructor(public authService: AuthService,
              public cartService: CartService,
              public utilService: UtilService,
              public venueService: VenueService,
              public selfServiceVenueService: SelfServiceVenueService,
              public legalPersonService: LegalPersonService,
              public validationService: ValidationService,
              public routeService: RouteService,
              public eventService: EventService,
              public viewService: ViewService,
              private router: Router,
              private storageService: StorageService,
              private dialog: MatDialog) {
    if (storageService.get(EventSearchComponent.STORAGE_SEARCH_RESULTS_VIEW_TYPE)) {
      this.viewType = storageService.get(EventSearchComponent.STORAGE_SEARCH_RESULTS_VIEW_TYPE);
    } else {
      if (utilService.isMobile) {
        this.viewType = 'table';
      }
    }
    if (storageService.get(EventSearchComponent.STORAGE_SEARCH_RESULTS_PAGE_SIZE)) {
      this.pageSize = parseInt(storageService.get(EventSearchComponent.STORAGE_SEARCH_RESULTS_PAGE_SIZE), 0);
    }
    if (storageService.get(EventSearchComponent.STORAGE_SEARCH_RESULTS_PAGE)) {
      this.page = parseInt(storageService.get(EventSearchComponent.STORAGE_SEARCH_RESULTS_PAGE), 0);
    }
    if (storageService.get(EventSearchComponent.STORAGE_SEARCH_RESULTS_SORT_BY)) {
      this.sortBy = parseInt(storageService.get(EventSearchComponent.STORAGE_SEARCH_RESULTS_SORT_BY), 0);
    }
  }

  public ngOnInit(): void {
    this.updateEventStatuses();
    this._subscriptions.push(this.utilService.userRoleSwitched.subscribe(() => this.updateEventStatuses()));

    if (this.isEventView) {
      this.isDetailedSearch = false;
    }
    if (this.authService.user?.hasDashboard && !this.isEventView) {
      this.search();
    }
  }
  public ngAfterViewInit(): void {
    this._subscriptions.push(this.router.events.subscribe((e) => {
      if (e instanceof NavigationEnd) {
        this.isDetailedSearch = !this.isEventView;
        if (e.urlAfterRedirects === '/app/sales-terminal' && this.authService.user) {
          this.clearFilters(true);
          if (this.authService.user.hasDashboard) {
            this.search();
          }
        }
      }
    }));
    if (!this.cartService.cartSubscription) {
      this.cartService.cartSubscription = this.cartService.getCart().subscribe();
    }
  }
  public ngOnDestroy(): void {
    this._subscriptions.forEach(s => s.unsubscribe());
    this._subscriptions = [];
  }

  public get sort(): SortByOption {
    return find(this.sortByOptions, {id: this.sortBy});
  }

  public get isSearchResults(): boolean {
    return this.routeService.checkRoute(['sales-terminal', 'search']);
  }

  public get isEventView(): boolean {
    return this.routeService.checkRoute(['sales-terminal', 'search', 'event']);
  }

  public get isFormValid(): boolean {
    if (this.authService.user.hasDashboard) {
      return true;
    }
    let check = true;
    if (!ValidationService.validateString(this.searchTerm, 3)) {
      check = false;
    }
    if (!check && this.isDetailedSearch) {
      let notEmpty = false;
      Object.keys(this.filters).forEach((key) => {
        if ((this.filters as any)[key]) {
          notEmpty = true;
        }
      });
      check = notEmpty;
    }
    return check;
  }

  public selectLegalPerson(): void {
    this.filters.legalPersonId = this.legalPersonScrollLoad?.object?.id;
    if (!this.filters.legalPersonId) {
      this.legalPersonScrollLoad.reset();
    }
  }

  public clearFilters(useDefaults: boolean = false): void {
    const filters = cloneDeep(this._defaultFilters);
    if (!useDefaults) {
      Object.keys(filters).forEach((key) => {
        (filters as any)[key] = null;
      });
    }
    this.searchTerm = '';
    this.venueScrollLoad.reset();
    this.selfServiceVenueScrollLoad.reset();
    this.legalPersonScrollLoad.reset();
    this.filters = filters;
  }

  public search(page = 1): void {
    this.showMobileFilters = false;
    this.isLoading = true;
    if (!this.isDetailedSearch) {
      this.filters = cloneDeep(this._defaultFilters);
      this.venueScrollLoad.reset();
      this.selfServiceVenueScrollLoad.reset();
      this.legalPersonScrollLoad.reset();
    }
    this.routeService.navigate('/app/sales-terminal/search').then(_ => {
      this.results = {items: [], total: 0};
      const filters = this.isDetailedSearch ? cloneDeep(this.filters) : {};
      this.page = page;
      if (filters.dateStart) {
        filters.dateStart = moment(filters.dateStart).format('YYYY-MM-DD');
      }
      if (filters.dateEnd) {
        filters.dateEnd = moment(filters.dateEnd).format('YYYY-MM-DD');
      }
      this.eventService.searchEventsPageable({
        q: this.searchTerm,
        page: this.page,
        pageSize: this.pageSize,
        sortBy: this.sort.field,
        sortOrder: this.sort.order,
        filters: filters
      }).pipe(
        finalize(() => this.isLoading = false)
      ).subscribe(results => this.results = results);
    });
  }

  public switchView(type: string): void {
    this.viewType = type;
    this.storageService.set(EventSearchComponent.STORAGE_SEARCH_RESULTS_VIEW_TYPE, type);
  }

  public updateLocalStorage(): void {
    this.search();
    this.storageService.set(EventSearchComponent.STORAGE_SEARCH_RESULTS_SORT_BY, this.sortBy);
    this.storageService.set(EventSearchComponent.STORAGE_SEARCH_RESULTS_PAGE_SIZE, this.pageSize);
  }

  private updateEventStatuses(): void {
    if (this.authService.canAccessSales) {
      this.eventService.findAllEventStatuses().subscribe((eventStatuses) => {
        this.eventStatuses = eventStatuses;
        if (!this.authService.checkPrivileges(Privileges.EVENT_VIEW_PREPARATION)) {
          this.eventStatuses = this.eventStatuses.filter(item => item.id !== STATUS_DRAFT);
        }
      });
    }
  }

  getStatusLabelById(statusId: number) {
    const status = this.eventStatuses.find(eventStatus => eventStatus.id === statusId);
    return status ? status.name : null;
  }

  toggleFilters() {
    this.showMobileFilters = !this.showMobileFilters;
  }

  public openEventDetails(event: Event): void {
    this.dialog.open(EventDetailsDialogComponent, {
      width: '60%',
      data: {
        event: event,
        displayNavigateToEvent: this.eventService.displayNavigateToEventView(event, this.cartService.cartDraftEvent)
      }
    }).afterClosed().subscribe((action) => {
      if (action === TO_EVENT_ACTION) {
        this.navigateToEventView(event.eventId);
      } else if (action === TO_EVENT_BO_VIEW) {
        this.navigateToSelfServiceEventView(event.eventId);
      }
    });
  }
  public openPurchaseView(event: Event): void {
    if (this.authService.isAliasSp) {
      this.cartService.checkIsOwnerAlias([event.eventId]).subscribe(data => {
        if (data.allowed) {
          const dialogRef = this.dialog.open(EventOwnerWarningDialogComponent, {
            width: '60%',
            panelClass: 'xs-popup',
            data: {event: event}
          });
          dialogRef.afterClosed().subscribe(() => {
            this.navigateToEventView(event.eventId);
          });
        } else {
          this.navigateToEventView(event.eventId);
        }
      });
    } else {
      this.navigateToEventView(event.eventId);
    }
  }
  public openEventBookings(event: Event): void {
    this.routeService.navigate('/app/sales-terminal/bookings')
      .then(() => this.utilService.bookingQueried.next({id: event.eventId, title: event.title}));
  }
  private navigateToEventView(eventId: number): void {
    this.routeService.navigate('/app/sales-terminal/search/event/' + eventId);
  }
  private navigateToSelfServiceEventView(eventId: number): void {
    this.routeService.navigate('/app/back-office/events/view/' + eventId);
  }

  protected readonly CartTypes = CartTypes;
}
